import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Container, useTheme, Card, CardContent, Divider, Button, IconButton } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import BusinessIcon from '@mui/icons-material/Business';
import EditOrganizationModal from '../../../../components/EditOrganizationModal'; // Импорт модального окна
import { useAuth } from '../../../../contexts/AuthContext';
import { UserType } from '../../../../types/auth';
import { ManagerDTO } from '../../../../types/manager';
import { OrganizationDTO } from '../../../../types/organization';
import { organizationService } from '../../../../services/organizationService';
import { userService } from '../../../../services/userService';
import styles from './OrganizationManagerDashboardPage.module.css';
import { useNavigate } from 'react-router-dom';
import OrganizationTasksPage from '../../tasks/OrganizationTasksPage'; // Импортируем компонент задач


import MainInfoIcon from './img/MainInfo.png';
import MainInfoIconActive from './img/MainInfoActive.png';
import TasksIcon from './img/MyTask.png';
import TasksIconActive from './img/MyTaskActive.png';
import DocAndFinIcon from './img/DocAndFin.png';
import DocAndFinIconActive from './img/DocAndFinActive.png';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const OrganizationManagerDashboardPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [organization, setOrganization] = useState<OrganizationDTO | null>(null);
    const [manager, setManager] = useState<ManagerDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { state } = useAuth();
    const authenticatedUser = state.user;
    const theme = useTheme();
    const navigate = useNavigate();

    const [isEditModalOpen, setIsEditModalOpen] = useState(false); // Состояние для управления видимостью модального окна

    const handleOpenEditModal = () => {
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
    };

    const handleSaveOrganization = async (updatedOrganization: OrganizationDTO) => {
        try {
            setLoading(true);
            setError(null);
            const response = await organizationService.updateOrganization(updatedOrganization);
            setOrganization(response); // Обновляем состояние организации в дашборде
            setIsEditModalOpen(false); // Закрываем модальное окно
        } catch (err: any) {
            console.error('Failed to update organization:', err);
            setError(err.response?.data?.message || 'Не удалось обновить данные организации.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchOrganizationData = async () => {
            if (authenticatedUser?.type === UserType.MANAGER) {
                setLoading(true);
                setError(null);
                
                if (authenticatedUser.manager?.organizationId) {
                    try {
                        const organizationId = authenticatedUser.manager.organizationId;
                        const orgResponse = await organizationService.getOrganizationById(organizationId);
                        setOrganization(orgResponse);
                    } catch (err: any) {
                        console.error('Failed to fetch organization data:', err);
                        setError(err.response?.data?.message || 'Не удалось загрузить данные организации.');
                    }
                } else {
                    // If organizationId is missing, just set organization to null, no error
                    setOrganization(null);
                }
                setManager(authenticatedUser.manager || null);
                setLoading(false);
            } else {
                setOrganization(null);
                setManager(null);
                setLoading(false);
            }
        };

        fetchOrganizationData();
    }, [authenticatedUser]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    if (loading) {
        return (
            <Container maxWidth="lg" className={styles.container}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <Typography>Загрузка...</Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" className={styles.container}>
                <Box sx={{ p: 4 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            </Container>
        );
    }

    if (!organization) {
        return (
            <Container maxWidth="lg" className={styles.container}>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        Ваша организация не найдена.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Чтобы начать работу, пожалуйста, создайте новую организацию.
                    </Typography>
                    <Button variant="contained" color="primary" size="large" onClick={() => alert('Функция создания организации пока не реализована.')}> {/* TODO: Implement actual navigation to organization creation form */}
                        Создать организацию
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" className={styles.container}>
            <Box className={styles.header}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                    Это профиль вашей организации на EAZZY:
                </Typography>
            </Box>

            <Box sx={{ width: '100%', mt: 4 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="organization dashboard tabs" className={styles.tabsContainer}>
                    <Tab
                        label="Основная информация"
                        icon={<img src={activeTab === 0 ? MainInfoIconActive : MainInfoIcon} alt="Основная информация" className={styles.tabIcon} />}
                        iconPosition="start"
                        {...a11yProps(0)}
                        className={`${styles.tab} ${activeTab === 0 ? styles.tabActive : ''}`}
                    />
                    <Tab
                        label="Задачи"
                        icon={<img src={activeTab === 1 ? TasksIconActive : TasksIcon} alt="Задачи" className={styles.tabIcon} />}
                        iconPosition="start"
                        {...a11yProps(1)}
                        className={`${styles.tab} ${activeTab === 1 ? styles.tabActive : ''}`}
                    />
                    <Tab
                        label="Документы и финансы"
                        icon={<img src={activeTab === 2 ? DocAndFinIconActive : DocAndFinIcon} alt="Документы и финансы" className={styles.tabIcon} />}
                        iconPosition="start"
                        {...a11yProps(2)}
                        className={`${styles.tab} ${activeTab === 2 ? styles.tabActive : ''}`}
                    />
                </Tabs>
                <TabPanel value={activeTab} index={0}>
                    <Box className={styles.infoCard}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {organization?.icon ? (
                                <img src={organization.icon} alt="Organization Icon" className={styles.organizationIcon} />
                            ) : (
                                <BusinessIcon sx={{ fontSize: 64 }} className={styles.organizationIcon} />
                            )}
                            <Box sx={{ ml: 2 }}>
                                <Typography variant="h6" className={styles.organizationName}>{organization?.name}</Typography>
                                {organization?.legalAddress && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary">{organization.legalAddress}</Typography>
                                    </Box>
                                )}
                                {organization?.directorFullName && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary">Директор: {organization.directorFullName}</Typography>
                                    </Box>
                                )}
                                {organization?.contactPersonFullName && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary">Контактное лицо: {organization.contactPersonFullName}</Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ ml: 'auto' }}>
                                <IconButton aria-label="edit organization" onClick={handleOpenEditModal}> 
                                    <EditIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2 }}>Платежные реквизиты:</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                            <Typography variant="body2" className={styles.label}>Наименование предприятия:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.name}</Typography>
                            
                            <Typography variant="body2" className={styles.label}>Фактический адрес:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.legalAddress}</Typography>

                            <Typography variant="body2" className={styles.label}>Телефон:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.phoneNumber}</Typography>

                            <Typography variant="body2" className={styles.label}>Номер расчетного счета:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.bankAccountNumber}</Typography>

                            <Typography variant="body2" className={styles.label}>Корреспондентский счет:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.correspondentAccount}</Typography>

                            <Typography variant="body2" className={styles.label}>Наименование банка:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.bankName}</Typography>

                            <Typography variant="body2" className={styles.label}>БИК:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.bic}</Typography>

                            <Typography variant="body2" className={styles.label}>ИНН / КПП:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.inn} / {organization?.kpp}</Typography>

                            <Typography variant="body2" className={styles.label}>ОГРН:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.ogrn}</Typography>
                        </Box>
                    </Box>
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                    {/* Здесь будет компонент задач организации */}
                    <OrganizationTasksPage organizationId={organization.id!} />
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                    {/* Placeholder for Документы и финансы */}
                    <Typography variant="h6">Документы и финансы</Typography>
                    <Typography>Здесь будут документы и финансовая информация.</Typography>
                </TabPanel>
            </Box>
            {organization && (
                <EditOrganizationModal
                    open={isEditModalOpen}
                    onClose={handleCloseEditModal}
                    organization={organization}
                    onSave={handleSaveOrganization} // Будет реализована на следующем шаге
                />
            )}
        </Container>
    );
};

export default OrganizationManagerDashboardPage;
